// Pan and zoom of the Image Viewer: the image is drawn at `scale` with its
// top-left corner at (x, y) in the viewer's pixels.

export interface ImageView { scale: number; x: number; y: number }

export const MAX_ZOOM = 16;

/** The whole image in the viewer, centred, never enlarged past its own size. */
export function fitView(viewW: number, viewH: number, imageW: number, imageH: number): ImageView {
  if (!(viewW > 0 && viewH > 0 && imageW > 0 && imageH > 0)) return { scale: 1, x: 0, y: 0 };
  const scale = Math.min(viewW / imageW, viewH / imageH, 1);
  return { scale, x: (viewW - imageW * scale) / 2, y: (viewH - imageH * scale) / 2 };
}

/** Zoom by `factor` keeping the image point under (px, py) where it is. */
export function zoomAt(view: ImageView, factor: number, px: number, py: number, minScale: number, maxScale = MAX_ZOOM): ImageView {
  const scale = Math.min(maxScale, Math.max(minScale, view.scale * factor));
  const k = scale / view.scale;
  return { scale, x: px - (px - view.x) * k, y: py - (py - view.y) * k };
}

export const zoomLabel = (scale: number): string => `${Math.round(scale * 100)}%`;

/**
 * The size a TIFF is decoded at: its own, or scaled down until the longest
 * side and the pixel count fit (a 100-megapixel scan would need ~400 MB of
 * canvas otherwise).
 */
export function tiffPreviewSize(width: number, height: number, maxSide = 8192, maxPixels = 40_000_000): { width: number; height: number } {
  const k = Math.min(1, maxSide / Math.max(width, height), Math.sqrt(maxPixels / (width * height)));
  return { width: Math.max(1, Math.floor(width * k + 1e-6)), height: Math.max(1, Math.floor(height * k + 1e-6)) };
}

/**
 * Decoded samples (1–4 a pixel, 8 or 16 bits or float) as the canvas's RGBA
 * bytes: grey repeated into the three colours, no alpha = opaque, deeper
 * samples scaled to 0–255 by `maxValue`.
 */
export function toRgba8(data: ArrayLike<number>, pixels: number, samples: number, maxValue: number): Uint8ClampedArray<ArrayBuffer> {
  const out = new Uint8ClampedArray(pixels * 4);
  const k = maxValue > 0 && maxValue !== 255 ? 255 / maxValue : 1;
  for (let i = 0; i < pixels; i++) {
    const s = i * samples;
    const o = i * 4;
    if (samples >= 3) {
      out[o] = data[s] * k; out[o + 1] = data[s + 1] * k; out[o + 2] = data[s + 2] * k;
      out[o + 3] = samples >= 4 ? data[s + 3] * k : 255;
    } else {
      const g = data[s] * k;
      out[o] = g; out[o + 1] = g; out[o + 2] = g;
      out[o + 3] = samples === 2 ? data[s + 1] * k : 255;
    }
  }
  return out;
}
